import { baseApi, RTKQueryTag } from '../../../data/baseApi'

export const ONE_OFF_FREQUENCY = 0

export type GetFlexibleAssessmentsArgs = {
  episodeId: string
}

export type SetFlexibleAssessmentArgs = {
  episode_id: string
  content_meta_data_id: string
  frequency: number
}

export type FlexibleAssessmentStatus = 'ACTIVE' | 'PAUSED'

export type UpdateFlexibleAssessmentArgs = {
  id: string
  previousFrequency: number
  previousStatus: FlexibleAssessmentStatus
} & (
  | { frequency: number; status?: FlexibleAssessmentStatus }
  | { frequency?: number; status: FlexibleAssessmentStatus }
)

export type FlexibleAssessment = {
  id: string
  episode_id: string
  content_meta_data_id: string
  content_meta_data_name: string | null
  status: FlexibleAssessmentStatus
  frequency: number
  last_assigned_session_number: number | null
  create_date: string
  update_date: string
}

const doesFlexibleAssessmentUpdateAffectAssignments = ({
  frequency,
  previousFrequency,
  previousStatus,
  status,
}: UpdateFlexibleAssessmentArgs) => {
  const nextFrequency = frequency ?? previousFrequency
  const nextStatus = status ?? previousStatus
  const crossesOneOffBoundary = (previousFrequency === ONE_OFF_FREQUENCY) !== (nextFrequency === ONE_OFF_FREQUENCY)
  // Landing on an active one-off always sends an assessment, including a re-send of one the client
  // already has - one-offs cannot be paused, so the frequency alone decides this.
  const isActiveOneOff = nextStatus === 'ACTIVE' && nextFrequency === ONE_OFF_FREQUENCY

  return crossesOneOffBoundary || isActiveOneOff
}

export const getFlexibleAssessmentUpdateInvalidatedTags = (error: unknown, update: UpdateFlexibleAssessmentArgs) => {
  if (error) return []

  return [
    RTKQueryTag.FLEXIBLE_ASSESSMENTS,
    ...(doesFlexibleAssessmentUpdateAffectAssignments(update) ? [RTKQueryTag.ASSIGNMENT] : []),
  ]
}

const flexibleAssessmentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getFlexibleAssessments: builder.query<FlexibleAssessment[], GetFlexibleAssessmentsArgs>({
      query: ({ episodeId }) => ({
        method: 'get',
        url: '/lt/v1/flexible_assessments',
        params: { episode_id: episodeId },
      }),
      providesTags: [RTKQueryTag.FLEXIBLE_ASSESSMENTS],
    }),
    setFlexibleAssessment: builder.mutation<FlexibleAssessment, SetFlexibleAssessmentArgs>({
      query: (data) => ({
        method: 'post',
        url: '/lt/v1/flexible_assessments',
        data,
        showAlert: false,
      }),
      invalidatesTags: [RTKQueryTag.ASSIGNMENT, RTKQueryTag.FLEXIBLE_ASSESSMENTS],
    }),
    updateFlexibleAssessment: builder.mutation<FlexibleAssessment, UpdateFlexibleAssessmentArgs>({
      query: ({ id, frequency, status }) => ({
        method: 'patch',
        url: `/lt/v1/flexible_assessments/${id}`,
        data: {
          ...(frequency === undefined ? {} : { frequency }),
          ...(status === undefined ? {} : { status }),
        },
        showAlert: false,
      }),
      invalidatesTags: (_result, error, update) => getFlexibleAssessmentUpdateInvalidatedTags(error, update),
    }),
  }),
})

export const { useGetFlexibleAssessmentsQuery, useSetFlexibleAssessmentMutation, useUpdateFlexibleAssessmentMutation } =
  flexibleAssessmentsApi
export default flexibleAssessmentsApi
